import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import {
    getMyAssignedJobs,
    subscribeToMyJobUpdates,
    isUserAssignmentCompleted,
    getUserAssignedQuotes
} from "../../lib/supabase/my-assignments";
import { getRequiredQuotes } from "../../lib/supabase/assignments";

import {
    FiCalendar, FiUser, FiPhone, FiTool, FiAlertCircle, FiEye,
    FiMail, FiPhoneOff, FiClock, FiCheckCircle, FiDollarSign,
    FiFlag, FiSearch, FiHome, FiLayers
} from "react-icons/fi";

const MyAssignments = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();

    const [assignments, setAssignments] = useState([]);
    const [filteredAssignments, setFilteredAssignments] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("newest");
    const [showCompleted, setShowCompleted] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    useEffect(() => {
        if (company?.id && userProfile?.id) {
            fetchMyAssignments();
        }
    }, [company, userProfile]);

    const fetchMyAssignments = async () => {
        try {
            const data = await getMyAssignedJobs(
                userProfile.id,
                userProfile.role,
                company.id
            );
            setAssignments(data);
            setFilteredAssignments(data);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Error fetching my assignments:", error);
            alert("Error fetching your assignments");
        }
    };

    // Real-time subscription
    useEffect(() => {
        if (!company?.id) return;

        const channel = subscribeToMyJobUpdates(company.id, (payload) => {
            console.log('Real-time update:', payload);
            fetchMyAssignments();
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [company?.id]);

    // Filter and search logic
    useEffect(() => {
        let filtered = [...assignments];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(
                (assignment) =>
                    assignment.unique_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    assignment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    assignment.carpenter_supervisor_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Completion filter
        if (!showCompleted) {
            filtered = filtered.filter(assignment =>
                !isUserAssignmentCompleted(assignment, userProfile.id, userProfile.role)
            );
        }

        // Sort
        switch (sortBy) {
            case "newest":
                filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case "oldest":
                filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                break;
            case "priority":
                filtered.sort((a, b) => {
                    const aPriority = a.urgent_quote || a.urgent_design;
                    const bPriority = b.urgent_quote || b.urgent_design;
                    return bPriority - aPriority;
                });
                break;
            default:
                break;
        }

        setFilteredAssignments(filtered);
    }, [assignments, searchTerm, sortBy, showCompleted, userProfile]);

    const getStatusColor = (status) => {
        const statusColors = {
            pending_design: "bg-amber-50 text-amber-700 border-amber-200",
            in_design: "bg-blue-50 text-blue-700 border-blue-200",
            design_completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
            pending_estimation: "bg-orange-50 text-orange-700 border-orange-200",
            in_estimation: "bg-purple-50 text-purple-700 border-purple-200",
            estimation_completed: "bg-green-50 text-green-700 border-green-200",
            ready_for_delivery: "bg-teal-50 text-teal-700 border-teal-200",
            completed: "bg-gray-50 text-gray-700 border-gray-200"
        };
        return statusColors[status] || "bg-gray-50 text-gray-700 border-gray-200";
    };

    const getStatusIcon = (status) => {
        const icons = {
            pending_design: <FiClock className="w-3.5 h-3.5" />,
            in_design: <FiClock className="w-3.5 h-3.5" />,
            design_completed: <FiCheckCircle className="w-3.5 h-3.5" />,
            pending_estimation: <FiClock className="w-3.5 h-3.5" />,
            in_estimation: <FiDollarSign className="w-3.5 h-3.5" />,
            estimation_completed: <FiCheckCircle className="w-3.5 h-3.5" />,
            ready_for_delivery: <FiCheckCircle className="w-3.5 h-3.5" />,
            completed: <FiCheckCircle className="w-3.5 h-3.5" />
        };
        return icons[status] || <FiClock className="w-3.5 h-3.5" />;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatRelativeTime = (date) => {
        if (!date) return 'Unknown time';

        const now = new Date();
        const inputDate = new Date(date);
        const diffMs = now - inputDate;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMinutes < 1) return 'just now';
        if (diffMinutes < 60) return `${diffMinutes}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return formatDate(date);
    };

    const handleViewAssignment = (assignmentId) => {
        navigate(`/${companySlug}/assignment/${assignmentId}`);
    };

    const FilterButton = ({ label, isSelected, onClick }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-lg border transition-all duration-200 font-medium text-sm ${isSelected
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
        >
            {label}
        </button>
    );

    // Assignment Icons Component
    const AssignmentIcons = ({ assignment }) => {
        if (!userProfile) return null;

        if (userProfile.role === 'designer') {
            const assignedQuotes = getUserAssignedQuotes(assignment, userProfile.id);

            return (
                <div className="flex items-center gap-1.5 flex-wrap">
                    {assignedQuotes.map((quote) => (
                        <div
                            key={quote.type}
                            className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${quote.isCompleted
                                ? 'bg-green-100 text-green-800'
                                : quote.isStarted
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                            title={`${quote.type.charAt(0).toUpperCase() + quote.type.slice(1)} - ${quote.isCompleted ? 'Completed' : quote.isStarted ? 'In Progress' : 'Not Started'
                                }`}
                        >
                            {quote.type === 'roof' && <FiHome className="w-3 h-3" />}
                            {quote.type === 'wall' && <FiLayers className="w-3 h-3" />}
                            {quote.type === 'floor' && <FiTool className="w-3 h-3" />}
                            {quote.isCompleted && <FiCheckCircle className="w-3 h-3" />}
                            <span>{quote.type.charAt(0).toUpperCase() + quote.type.slice(1)}</span>
                        </div>
                    ))}
                </div>
            );
        } else if (userProfile.role === 'estimator') {
            const estimator = assignment.job_estimators?.find(e => e.user_id === userProfile.id);

            if (estimator) {
                return (
                    <div className="flex items-center gap-1.5">
                        <div
                            className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${estimator.is_completed
                                ? 'bg-green-100 text-green-800'
                                : estimator.is_started
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                            title={`Estimator - ${estimator.is_completed ? 'Completed' : estimator.is_started ? 'In Progress' : 'Not Started'}`}
                        >
                            <FiDollarSign className="w-3 h-3" />
                            {estimator.is_completed && <FiCheckCircle className="w-3 h-3" />}
                            <span>Estimation</span>
                        </div>
                    </div>
                );
            }
        }

        return null;
    };

    const isCompleted = (job) => isUserAssignmentCompleted(job, userProfile?.id, userProfile?.role);

    return (
        <div className="min-h-screen">
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">My Assignments</h1>
                            <p className="text-sm text-gray-600 mt-1">
                                Last updated {formatRelativeTime(lastUpdated)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    {/* Search */}
                    <div className="mb-4">
                        <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search by Job ID, Customer, or Supervisor..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Sort:</span>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                    <option value="priority">Priority First</option>
                                </select>
                            </div>

                            {/* Completion Filter */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Show:</span>
                                <FilterButton
                                    label="All Jobs"
                                    isSelected={showCompleted}
                                    onClick={() => setShowCompleted(true)}
                                />
                                <FilterButton
                                    label="Hide Completed"
                                    isSelected={!showCompleted}
                                    onClick={() => setShowCompleted(false)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                        <span className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredAssignments.length}</span> of{' '}
                            <span className="font-semibold text-gray-900">{assignments.length}</span> assignments
                        </span>
                    </div>
                </div>

                {/* Assignments List */}
                <div className="space-y-3">
                    {filteredAssignments.length === 0 ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiAlertCircle className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No assignments found</h3>
                            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        filteredAssignments.map((job) => (
                            <div
                                key={job.id}
                                onClick={() => handleViewAssignment(job.id)}
                                className={`bg-white rounded-lg shadow-sm border transition-all duration-200 cursor-pointer ${isCompleted(job)
                                    ? 'border-green-300 bg-green-50 hover:shadow-md hover:border-green-400'
                                    : 'border-gray-200 hover:shadow-md hover:border-blue-300'
                                    }`}
                            >
                                <div className="p-4">
                                    {/* Header Row */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-start gap-3 flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-semibold text-gray-900 text-base">
                                                    {job.unique_id}
                                                </h3>
                                                {(job.urgent_quote || job.urgent_design) && (
                                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold flex items-center gap-1">
                                                        <FiFlag className="w-3 h-3" />
                                                        URGENT
                                                    </span>
                                                )}
                                                {isCompleted(job) && (
                                                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold flex items-center gap-1">
                                                        <FiCheckCircle className="w-3 h-3" />
                                                        Completed
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <span className={`px-3 py-1 rounded-md text-xs font-medium border flex items-center gap-1.5 whitespace-nowrap ${getStatusColor(job.main_status)}`}>
                                            {getStatusIcon(job.main_status)}
                                            {job.main_status.replace(/_/g, ' ').toUpperCase()}
                                        </span>
                                    </div>

                                    {/* Assignment Icons */}
                                    <div className="mb-3">
                                        <AssignmentIcons assignment={job} />
                                    </div>

                                    {/* Content Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                        {/* Customer Info */}
                                        <div className="md:col-span-3">
                                            <div className="flex items-start gap-2">
                                                <FiUser className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {job.customer_name}
                                                    </p>
                                                    <div className="flex items-center gap-1.5 mt-1">
                                                        {job.customer_number && (
                                                            <a
                                                                href={`tel:${job.customer_number}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                                            >
                                                                <FiPhone className="w-3 h-3" />
                                                                Call
                                                            </a>
                                                        )}
                                                        {job.customer_mail && (
                                                            <a
                                                                href={`mailto:${job.customer_mail}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                                                            >
                                                                <FiMail className="w-3 h-3" />
                                                                Email
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Supervisor Info */}
                                        <div className="md:col-span-3">
                                            <div className="flex items-start gap-2">
                                                <FiTool className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {job.carpenter_supervisor_name || "No supervisor"}
                                                    </p>
                                                    {job.carpenter_supervisor_number ? (
                                                        <a
                                                            href={`tel:${job.carpenter_supervisor_number}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 mt-1"
                                                        >
                                                            <FiPhone className="w-3 h-3" />
                                                            Call Site
                                                        </a>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                                            <FiPhoneOff className="w-3 h-3" />
                                                            No contact
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Job Details */}
                                        < div className="md:col-span-3" >
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                                    {job.job_type}
                                                </span>
                                                {getRequiredQuotes(job).map((quote) => (
                                                    <span key={quote} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                                        {quote}
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500">
                                                <FiCalendar className="w-3 h-3" />
                                                {formatDate(job.created_at)}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="md:col-span-3 flex items-center justify-end">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewAssignment(job.id);
                                                }}
                                                className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium flex items-center gap-2"
                                            >
                                                <FiEye className="w-4 h-4" />
                                                View
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div >
                        ))
                    )}
                </div >
            </div >
        </div >
    );
};

export default MyAssignments;